import BaseService from "react-lib/apis/services/BaseService";

import { getPurRecord, PurRecord, PurRequestLine } from "./purApi";
import { canUsePurDraftCreatePilot } from "./purDraftCreate";
import { getPur, PUR_REQUEST_TIMEOUT_MS, PurClientConfig, purRequestOptions, readId, readObject } from "./purRead";

// Legacy contract only. Form values are retained for disclosure/read-back, not
// forwarded as unsupported keys or disguised as a standalone PO CREATE.
export interface PurRequestOrderInput {
  mrId: number; editedAt: string; projectId: number; storageId: number; vendorId: number;
  lines: Array<{ requestItemId: number; productId: number; unitId: number; factor: string; remaining: string; quantity: string; unitCost: string }>;
}
export interface PurRequestOrderPermission { resource: boolean; create: boolean; order: boolean; readMr: boolean; readPo: boolean }
export interface PurRequestOrderAttempt { input: PurRequestOrderInput; source: PurRecord; actorUserId: number; requiresOrder: boolean }
export interface PurRequestOrderResult {
  po: PurRecord; mr: PurRecord;
  differences: Array<{ requestItemId: number; field: "quantity" | "unitCost" | "factor"; form: string; actual: string }>;
}
export const purRequestOrderPermissionNames = {
  resource: "users.CREATE_PUR:material-request-batch-create-po",
  create: "users.ACTION_PUR.PurchaseOrder:CREATE",
  order: "users.ACTION_PUR.MaterialRequest:ORDER",
  readMr: "users.RETRIEVE_PUR:material-request-detail",
  readPo: "users.RETRIEVE_PUR:purchase-order-detail",
};
const permissionKeys: Record<keyof PurRequestOrderPermission, string> = {
  resource: "perm_users_CREATE_PUR:material-request-batch-create-po",
  create: "action_PUR_PurchaseOrder_CREATE", order: "action_PUR_MaterialRequest_ORDER",
  readMr: "perm_users_RETRIEVE_PUR:material-request-detail", readPo: "perm_users_RETRIEVE_PUR:purchase-order-detail",
};
export class PurRequestOrderError extends Error {
  constructor(public outcome: "blocked" | "rejected" | "unknown", public messageId: string, public poIds: number[] = []) {
    super(messageId); Object.setPrototypeOf(this, PurRequestOrderError.prototype);
  }
}
const block = (id = "MEERP_MR_PO_LEGACY_SCOPE"): never => { throw new PurRequestOrderError("blocked", id); };
const units = (value: string | null, places = 2): number => {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value?.trim() || "");
  if (!match || (match[2] || "").slice(places).replace(/0/g, "")) return block("MEERP_MR_PO_SOURCE_ERROR");
  const result = Number(`${match[1]}${(match[2] || "").slice(0, places).padEnd(places, "0")}`);
  return Number.isSafeInteger(result) ? result : block("MEERP_MR_PO_SOURCE_ERROR");
};
const same = (left: string | null, right: string | null, places = 2): boolean => {
  try { return units(left, places) === units(right, places); } catch { return false; }
};
export const buildLegacyRequestOrderPayload = (input: PurRequestOrderInput) => {
  if (readId(input.mrId) === null || readId(input.projectId) === null || readId(input.storageId) === null
    || readId(input.vendorId) === null || !input.lines.length || input.lines.length > 100) return block();
  const ids = new Set<number>();
  input.lines.forEach((line) => {
    if (readId(line.requestItemId) === null || readId(line.productId) === null || readId(line.unitId) === null || ids.has(line.requestItemId)
      || units(line.quantity) <= 0 || units(line.remaining) < units(line.quantity) || units(line.factor, 4) <= 0) return block("MEERP_MR_PO_SOURCE_ERROR");
    if (line.unitCost.trim()) units(line.unitCost);
    ids.add(line.requestItemId);
  });
  return { request_items: [...ids], delivery_storage: input.storageId, default_vendor: input.vendorId };
};
export const checkPurRequestOrderPermissions = async (client: PurClientConfig): Promise<PurRequestOrderPermission> => {
  if (!canUsePurDraftCreatePilot(client)) return block();
  const { url, headers } = purRequestOptions({ ...client, pathPrefix: "/users/apis" }, "user-permission/");
  const query = Object.fromEntries(Object.values(permissionKeys).map((key) => [key, false]));
  const response = await new BaseService().client.post(url, query, { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
  const data = readObject(response.data);
  return { resource: data[permissionKeys.resource] === true, create: data[permissionKeys.create] === true, order: data[permissionKeys.order] === true,
    readMr: data[permissionKeys.readMr] === true, readPo: data[permissionKeys.readPo] === true };
};
export const missingPurRequestOrderPermissions = (permission: PurRequestOrderPermission, requiresOrder = false): string[] =>
  (Object.keys(permissionKeys) as Array<keyof PurRequestOrderPermission>)
    .filter((key) => !permission[key] && (key !== "order" || requiresOrder)).map((key) => purRequestOrderPermissionNames[key]);

const requestLines = (record: PurRecord): PurRequestLine[] => {
  if (!record.items.length || record.items.some((line) => line.kind !== "materialRequests" || readId(line.id) === null || line.requestId !== record.id)) return block("MEERP_MR_PO_SOURCE_ERROR");
  return record.items as PurRequestLine[];
};
const scopedMr = (client: PurClientConfig, record: PurRecord, input: PurRequestOrderInput) => {
  if (!canUsePurDraftCreatePilot(client) || record.id !== input.mrId || record.projectId !== input.projectId
    || readId(record.storageId) !== input.storageId || readId(record.requestingDivisionId) !== readId(client.divisionId)) return block();
};
export const preparePurRequestOrder = async (client: PurClientConfig, input: PurRequestOrderInput): Promise<PurRequestOrderAttempt> => {
  if (!canUsePurDraftCreatePilot(client)) return block();
  buildLegacyRequestOrderPayload(input);
  const permission = await checkPurRequestOrderPermissions(client);
  if (missingPurRequestOrderPermissions(permission).length) return block("MEERP_MR_PO_LEGACY_PERMISSION");
  const profile = readObject(await getPur({ ...client, pathPrefix: "/users/apis" }, "users/profile/"));
  const actorUserId = readId(profile.user);
  if (actorUserId === null) return block("MEERP_MR_PO_LEGACY_ACTOR");
  const source = await getPurRecord(client, "materialRequests", input.mrId);
  scopedMr(client, source, input);
  if (!["APPROVED", "PARTIALLY_ORDERED"].includes(source.status) || (input.editedAt && source.editedAt !== input.editedAt)) return block("MEERP_MR_PO_LEGACY_STALE");
  const lines = requestLines(source);
  const ids = new Set(input.lines.map((line) => line.requestItemId));
  if (new Set(lines.map((line) => line.id)).size !== lines.length) return block("MEERP_MR_PO_SOURCE_ERROR");
  lines.forEach((line) => { if (units(line.requestedQuantity) < units(line.orderedQuantity)) return block("MEERP_MR_PO_SOURCE_ERROR"); });
  input.lines.forEach((line) => {
    const actual = lines.find((item) => item.id === line.requestItemId);
    if (!actual || actual.productId !== line.productId || actual.unitId !== line.unitId || !same(actual.conversionFactor, line.factor, 4)
      || units(actual.requestedQuantity) - units(actual.orderedQuantity) !== units(line.remaining)) return block("MEERP_MR_PO_LEGACY_STALE");
  });
  // ORDER is invoked by today's service only when all remaining MR lines are
  // selected. Its partial branch bypasses that action; this remains an issue.
  const requiresOrder = lines.every((line) => units(line.requestedQuantity) === units(line.orderedQuantity) || ids.has(line.id!));
  if (missingPurRequestOrderPermissions(permission, requiresOrder).length) return block("MEERP_MR_PO_LEGACY_ORDER_PERMISSION");
  const project = readObject(await getPur(client, `projects/${input.projectId}/`));
  const vendor = readObject(await getPur(client, `vendors/${input.vendorId}/`));
  if (readId(project.id) !== input.projectId || readId(project.division) !== readId(client.divisionId) || project.active !== true
    || readId(vendor.id) !== input.vendorId) return block();
  return { input, source, actorUserId, requiresOrder };
};

export const reconcilePurRequestOrder = async (client: PurClientConfig, attempt: PurRequestOrderAttempt, poId: number): Promise<PurRequestOrderResult> => {
  if (!canUsePurDraftCreatePilot(client) || readId(poId) === null) return block();
  const { input, source } = attempt;
  buildLegacyRequestOrderPayload(input);
  const [po, mr] = await Promise.all([getPurRecord(client, "purchaseOrders", poId), getPurRecord(client, "materialRequests", input.mrId)]);
  const unknown = (): never => { throw new PurRequestOrderError("unknown", "MEERP_MR_PO_LEGACY_UNKNOWN", [poId]); };
  scopedMr(client, mr, input);
  if (po.id !== poId || po.status === "CANCELLED" || po.projectId !== input.projectId || readId(po.storageId) !== input.storageId
    || po.vendorId !== input.vendorId || po.items.length !== input.lines.length || new Set(po.items.map((item) => item.id)).size !== po.items.length) return unknown();
  const before = requestLines(source), after = requestLines(mr);
  if (before.length !== after.length || new Set(after.map((line) => line.id)).size !== after.length
    || mr.status !== (attempt.requiresOrder ? "ORDERED" : "PARTIALLY_ORDERED")) return unknown();
  before.forEach((old) => {
    const current = after.find((line) => line.id === old.id);
    const selected = input.lines.some((line) => line.requestItemId === old.id);
    if (!current || current.productId !== old.productId || current.unitId !== old.unitId
      || !same(current.conversionFactor, old.conversionFactor, 4) || !same(current.requestedQuantity, old.requestedQuantity)
      || (!selected && (current.poItemId !== old.poItemId || !same(current.orderedQuantity, old.orderedQuantity)))) return unknown();
  });
  const differences: PurRequestOrderResult["differences"] = [];
  input.lines.forEach((form) => {
    const old = before.find((line) => line.id === form.requestItemId), linked = after.find((line) => line.id === form.requestItemId);
    const item = po.items.find((line) => line.id === linked?.poItemId);
    if (!old || !linked || !item || item.kind !== "purchaseOrders" || item.id === null || item.id === old.poItemId || item.poId !== po.id
      || item.productId !== form.productId || item.unitId !== form.unitId || linked.productId !== form.productId || linked.unitId !== form.unitId
      || !same(old.requestedQuantity, linked.requestedQuantity) || units(item.orderedQuantity) <= 0 || units(item.orderedQuantity) > units(form.remaining)
      || units(linked.orderedQuantity) !== units(old.orderedQuantity) + units(item.orderedQuantity)
      || units(item.conversionFactor, 4) <= 0 || item.unitCost === null) return unknown();
    units(item.unitCost);
    const values = { quantity: item.orderedQuantity!, unitCost: item.unitCost, factor: item.conversionFactor! };
    (["quantity", "unitCost", "factor"] as const).forEach((field) => {
      if (form[field].trim() && !same(form[field], values[field], field === "factor" ? 4 : 2)) differences.push({ requestItemId: form.requestItemId, field, form: form[field], actual: values[field] });
    });
  });
  return { po, mr, differences };
};

export const createPurRequestOrder = async (client: PurClientConfig, attempt: PurRequestOrderAttempt, isCurrent: () => boolean = () => true): Promise<PurRequestOrderResult> => {
  let fresh: PurRequestOrderAttempt;
  try { fresh = await preparePurRequestOrder(client, attempt.input); }
  catch (error: unknown) {
    if (error instanceof PurRequestOrderError) throw error;
    // Preflight failed before the mutation boundary; no create was attempted.
    return block("MEERP_MR_PO_LEGACY_CHECK_ERROR");
  }
  if (!isCurrent() || fresh.actorUserId !== attempt.actorUserId) return block("MEERP_MR_PO_LEGACY_CONTEXT");
  if (JSON.stringify(fresh.source) !== JSON.stringify(attempt.source)) return block("MEERP_MR_PO_LEGACY_STALE");
  const { url, headers } = purRequestOptions(client, "material-requests/batch-create-po/");
  let poIds: number[] = [];
  try {
    const response = await new BaseService().client.post(url, buildLegacyRequestOrderPayload(attempt.input), { headers, timeout: PUR_REQUEST_TIMEOUT_MS });
    if (Array.isArray(response.data)) poIds = response.data.map((row: unknown) => readId(readObject(row).id)).filter((id: number | null): id is number => id !== null);
    if (![200, 201].includes(response.status) || !Array.isArray(response.data) || response.data.length !== 1 || poIds.length !== 1) throw new Error("Unexpected legacy PO response");
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    if (status && [401, 403, 404, 405].includes(status)) throw new PurRequestOrderError("rejected", "MEERP_MR_PO_LEGACY_REJECTED");
    // The view catches any Exception as 400, including serialization after the
    // atomic utility committed. Even a 400 is not proof that nothing was saved.
    throw new PurRequestOrderError("unknown", "MEERP_MR_PO_LEGACY_UNKNOWN", poIds);
  }
  try { return await reconcilePurRequestOrder(client, attempt, poIds[0]); }
  catch { throw new PurRequestOrderError("unknown", "MEERP_MR_PO_LEGACY_UNKNOWN", poIds); }
};
